import React, { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { Modal, Button, Nav, Tab, Spinner } from 'react-bootstrap';
import { IconCheck, IconUpload, IconTrash, IconMoodSmile, IconUserCheck } from '@tabler/icons-react';
import { ALL_AVATARS, AVATAR_CATEGORIES } from 'utils/avatarOptions';
import Avatar from 'components/general/Avatar';
import './AvatarPickerModal.scss';

const AvatarPickerModal = ({
  show,
  onHide,
  currentImage,
  userName,
  userRole,
  onSelectAvatar,
  onRemoveAvatar,
  isLoading,
}) => {
  const [selectedUrl, setSelectedUrl] = useState(currentImage || '');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const fileInputRef = useRef(null);

  const handleSelectPreset = (url) => {
    setSelectedUrl(url);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      alert('Please select a valid image file.');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      alert('Image size must be less than 5MB.');
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setSelectedUrl(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const handleSave = () => {
    if (selectedUrl) {
      onSelectAvatar(selectedUrl);
    }
  };

  const filteredAvatars = ALL_AVATARS.filter(
    (av) => selectedCategory === 'all' || av.category === selectedCategory
  );

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      dialogClassName="ios-avatar-modal"
    >
      <Modal.Header closeButton className="border-0 pb-0 pt-4 px-4">
        <Modal.Title className="w-100 text-center">
          <div className="d-flex align-items-center justify-content-center gap-2">
            <IconMoodSmile className="text-primary" size={24} />
            <span className="fw-bold" style={{ fontSize: '18px', letterSpacing: '-0.3px' }}>
              Choose Avatar, Memoji or Photo
            </span>
          </div>
          <p className="text-muted small mb-0 mt-1" style={{ fontSize: '13px', fontWeight: 500 }}>
            Select Memojis, Robots, Animals, Skeletons, Fruits or upload a custom photo
          </p>
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className="px-4 py-3">
        {/* Live Avatar Preview */}
        <div className="preview-container text-center mb-3">
          <div className="d-inline-block position-relative">
            <div className="preview-ring">
              <Avatar
                name={userName}
                role={userRole}
                src={selectedUrl || currentImage}
                style={{ width: '90px', height: '90px', fontSize: '2rem' }}
              />
            </div>
            {selectedUrl && (
              <span className="active-badge">
                <IconCheck size={14} />
              </span>
            )}
          </div>
        </div>

        {/* Main Tab Selector */}
        <Tab.Container defaultActiveKey="presets">
          <Nav variant="pills" className="ios-nav-pills justify-content-center mb-3">
            <Nav.Item>
              <Nav.Link eventKey="presets" className="d-flex align-items-center gap-2">
                <IconMoodSmile size={16} /> Presets & Memojis
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="upload" className="d-flex align-items-center gap-2">
                <IconUpload size={16} /> Custom Photo
              </Nav.Link>
            </Nav.Item>
          </Nav>

          <Tab.Content>
            {/* Presets Tab */}
            <Tab.Pane eventKey="presets">
              {/* Category Filter Chips */}
              <div className="category-pills d-flex align-items-center gap-1 overflow-auto pb-2 mb-2" style={{ scrollbarWidth: 'none' }}>
                {AVATAR_CATEGORIES.map((cat) => (
                  <button
                    key={cat.id}
                    type="button"
                    className={`btn btn-sm text-nowrap rounded-pill border-0 ${selectedCategory === cat.id ? 'bg-primary text-white fw-bold shadow-sm' : 'bg-light text-muted'}`}
                    style={{ fontSize: '12px', padding: '5px 14px', transition: 'all 0.2s ease' }}
                    onClick={() => setSelectedCategory(cat.id)}
                  >
                    {cat.label}
                  </button>
                ))}
              </div>

              {/* Avatars Grid */}
              <div className="memoji-grid">
                {filteredAvatars.map((avatar) => {
                  const isSelected = selectedUrl === avatar.url;
                  return (
                    <div
                      key={avatar.id}
                      className={`memoji-item ${isSelected ? 'selected' : ''}`}
                      onClick={() => handleSelectPreset(avatar.url)}
                      title={avatar.label}
                    >
                      <img src={avatar.url} alt={avatar.label} loading="lazy" />
                      {isSelected && (
                        <div className="check-overlay">
                          <IconCheck size={14} />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </Tab.Pane>

            {/* Custom Upload Tab */}
            <Tab.Pane eventKey="upload">
              <div
                className="upload-dropzone text-center p-4"
                onClick={() => fileInputRef.current?.click()}
              >
                <div className="upload-icon-wrap mb-3">
                  <IconUpload size={28} className="text-primary" />
                </div>
                <h6 className="fw-bold mb-1">Click to Upload Custom Photo</h6>
                <p className="text-muted small mb-0">PNG, JPG or WebP (max 5MB)</p>
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileChange}
                  accept="image/*"
                  style={{ display: 'none' }}
                />
              </div>
            </Tab.Pane>
          </Tab.Content>
        </Tab.Container>
      </Modal.Body>

      <Modal.Footer className="border-0 px-4 pb-4 pt-0 d-flex justify-content-between">
        {currentImage ? (
          <Button
            variant="outline-danger"
            size="sm"
            className="d-flex align-items-center gap-1 border-0 fw-semibold"
            onClick={onRemoveAvatar}
            disabled={isLoading}
            style={{ borderRadius: '10px' }}
          >
            <IconTrash size={16} /> Remove Photo
          </Button>
        ) : (
          <div></div>
        )}

        <div className="d-flex gap-2">
          <Button
            variant="light"
            onClick={onHide}
            style={{ borderRadius: '10px', fontWeight: 600 }}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSave}
            disabled={isLoading || !selectedUrl}
            style={{ borderRadius: '10px', fontWeight: 700, paddingLeft: '20px', paddingRight: '20px' }}
          >
            {isLoading ? <Spinner animation="border" size="sm" /> : <><IconUserCheck size={16} className="me-1" /> Apply Avatar</>}
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

AvatarPickerModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  currentImage: PropTypes.string,
  userName: PropTypes.string,
  userRole: PropTypes.string,
  onSelectAvatar: PropTypes.func.isRequired,
  onRemoveAvatar: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
};

export default AvatarPickerModal;
